// Copyright (c) StrangeBee 2024

import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ExtractProps } from '@Molecules/Details/Details';
import { FormatDate } from '@Molecules/Format';
import React, { ReactElement } from 'react';
import { TaskStatus } from 'thehive-sdk';
import { Space } from 'antd';
import { Link } from 'react-router-dom';
import THTag from '@Atoms/THTag/THTag';
import { classList } from '@Helpers/className';
import { TaskStatusColors } from '@Constants/colors';
import { ICONS } from '@Constants/iconsApp';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { cutString } from '@Helpers/string';

interface ExtractDataProps {
    locale: (...args: any[]) => any;
}

export const extractData = ({ locale }: ExtractDataProps): Record<string, ExtractProps> => ({
    '#computeTitle': {
        name: '',
        render: ({ title, path, icon }: { title: string; path: string; icon: ReactElement }): ReactElement => (
            <div className="result-card__title">
                <Space>
                    {icon}
                    <Link to={path}>{title}</Link>
                </Space>
            </div>
        ),
    },
    '#computeDate': {
        name: '',
        render: ({ date, assignee }: { date: number; assignee: string }): ReactElement => (
            <div className="result-card__date">
                <FormatDate icon date={date} />
                {assignee && <AssigneeInfo assignee={assignee} />}
            </div>
        ),
    },
    '#computeStatus': {
        name: '',
        isForce: false,
        render: ({
            status,
            startDate,
            endDate,
            _createdAt,
        }: {
            status: TaskStatus;
            startDate: number;
            endDate: number;
            _createdAt: number;
        }): ReactElement => (
            <div className="task-result-card__status-container">
                <div
                    className={classList('task-result-card__status', {
                        waiting: status === TaskStatus.WAITING,
                        'in-progress': status === TaskStatus.IN_PROGRESS,
                        completed: status === TaskStatus.COMPLETED,
                    })}
                >
                    <THTag
                        className="task-result-card__status-tag"
                        color={TaskStatusColors[status]}
                        full
                        value={status}
                    />

                    <div className="task-result-card__status__date">
                        <ICONS.ClockCircleOutlined />
                        <FormatDate
                            date={(status === TaskStatus.IN_PROGRESS ? startDate : endDate) || _createdAt}
                            onlyFrom
                        />
                    </div>
                </div>
            </div>
        ),
    },
    '#computeDescription': {
        name: '',
        isForce: true,
        render: (description: string): ReactElement => (
            <div className="result-card__description">
                {description === 'N/A' ? locale('common.noDescription') : cutString(description, 140)}
            </div>
        ),
    },
    '#computeTags': {
        name: '',
        isForce: false,
        render: (tags: string[]): ReactElement => (
            <DisplayTag.List data={tags} limit={5}>
                {(tag) => <DisplayTag tag={tag} />}
            </DisplayTag.List>
        ),
    },
});
